import React, { useState } from 'react';

import {
  Box,
  Card,
  CardActions,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Typography,
  Link,
  Button,
  ClickAwayListener,
} from '@mui/material';
import { Tooltip, useMediaQuery, makeStyles } from '@material-ui/core';
import LockIcon from '@mui/icons-material/Lock';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import { COLOR_PRIMARY, exchangeRates } from 'src/const';
import { convertCurrency, formatCurrency } from 'src/utils/functions';
import { COLOR_GREY_600, COLOR_TEXT_2, COLOR_TEXT_3 } from 'src/colors';

const useStylesRoot = makeStyles({
  tooltip: {
    padding: 0,
  },
});

const HeatmapPointerTooltipCard = ({
  currency,
  metric,
  metricValue,
  userhasDataAccess,
  handleOnSampleClick,
  pointerText,
}: {
  currency: string;
  metric: string;
  metricValue: number;
  userhasDataAccess: boolean;
  handleOnSampleClick;
  pointerText: string;
}) => {
  console.log('metricValue', metricValue);

  return (
    <Card sx={{ width: '100%', maxWidth: '400px' }}>
      <CardContent>
        {pointerText && (
          <Typography gutterBottom variant="body2" sx={{ fontWeight: 'bold' }}>
            {pointerText}
          </Typography>
        )}
        <Typography gutterBottom variant="body2">
          <span style={{ fontSize: '12px' }}>{metric}:</span>{' '}
          {userhasDataAccess ? (
            <span style={{ fontWeight: 'bold' }}>
              {formatMetricValue(metricValue, metric, currency)}
            </span>
          ) : (
            <IconButton onClick={() => handleOnSampleClick('expert')}>
              <LockIcon color="primary" />
            </IconButton>
          )}
        </Typography>
      </CardContent>
    </Card>
  );
};

const formatMetricValue = (metricValue: number, metric: string, currency: string) => {
  if (metric === 'Revenue') {
    const formattedValue = formatCurrency(String(convertCurrency(currency, metricValue)));
    return `${exchangeRates[currency].symbol}${formattedValue}`;
  } else if (metric === 'Occupancy Rate') {
    return `${metricValue}%`;
  } else {
    return metricValue;
  }
};

const HeatmapPointer = ({
  lat,
  lng,
  pointerText,
  metric,
  metricValue,
  currency,
  userhasDataAccess,
  handleOnSampleClick,
  scale,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const matches = useMediaQuery('(max-width:550px)');
  const rootClass = useStylesRoot();

  const icon = (
    <Box
      className="discovery-score-card"
      sx={{
        position: 'relative',
        minWidth: '50px',
        maxWidth: '135px',
        height: '35px',
        background: '#fff',
        color: '#000',
        borderRadius: 100,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxShadow: '0 2px 4px #0004, 0 2px 10px #0002',
        transform: `translate(-50%, -50%) scale(${scale})`,
        transition: 'background 0.3s ease, color 0.3s ease',
        overflow: 'clip',
        '&:hover': {
          background: '#000',
          color: '#fff',
        },
        padding: '0 20px',
      }}
    >
      <pre
        style={{
          paddingRight: '2px',
          textOverflow: 'ellipsis',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            width: '50px',
            textAlign: 'center',
            overflow: 'clip',
            textOverflow: 'ellipsis',
          }}
        >
          {pointerText ? pointerText : formatMetricValue(metricValue, metric, currency)}
        </Typography>
      </pre>
    </Box>
  );

  return (
    <ClickAwayListener
      onClickAway={(event: MouseEvent | TouchEvent) => {
        const target: any = event.target as HTMLElement;
        const isTooltip = target.closest && target.closest('.MuiTooltip-tooltip');

        if (tooltipOpen && !isTooltip) {
          setTooltipOpen(false);
        }
      }}
    >
      <Tooltip
        interactive
        disableTouchListener
        disableFocusListener={matches ? true : false}
        classes={{
          tooltip: rootClass.tooltip,
        }}
        open={tooltipOpen}
        onOpen={() => setTooltipOpen(true)}
        onClose={() => {
          !matches && setTooltipOpen(false);
        }}
        style={{
          zIndex: 999999,
        }}
        enterTouchDelay={0}
        leaveTouchDelay={5000}
        title={
          <HeatmapPointerTooltipCard
            currency={currency}
            metric={metric}
            metricValue={metricValue}
            userhasDataAccess={userhasDataAccess}
            handleOnSampleClick={handleOnSampleClick}
            pointerText={pointerText}
          />
        }
      >
        {icon}
      </Tooltip>
    </ClickAwayListener>
  );
};

export default HeatmapPointer;
